import React, { useEffect, useState } from 'react';

interface ZoneAnchor {
  id: string;
  label: string;
}

const ZONES: ZoneAnchor[] = [
  { id: 'team-zone-access', label: 'Access' },
  { id: 'team-zone-capacity', label: 'Capacity' },
  { id: 'team-zone-people', label: 'People' },
  { id: 'team-zone-distribution', label: 'Distribution' },
];

/**
 * Sticky in-tab navigation strip that scrolls to each zone and tracks the
 * currently visible zone via IntersectionObserver. Hidden zones are silently
 * skipped (e.g. owner-only Access zone for non-owners).
 */
export const TeamTabAnchorStrip: React.FC = () => {
  const [activeZone, setActiveZone] = useState<string | null>(null);
  const [visibleZones, setVisibleZones] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visible = new Set<string>();

    for (const zone of ZONES) {
      const el = document.getElementById(zone.id);
      if (!el) continue;
      visible.add(zone.id);

      const obs = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setActiveZone(zone.id);
              break;
            }
          }
        },
        {
          // Trigger when the zone is roughly under the sticky strip
          rootMargin: '-100px 0px -50% 0px',
          threshold: [0, 0.1, 0.5],
        }
      );
      obs.observe(el);
      observers.push(obs);
    }

    setVisibleZones(visible);
    if (!activeZone && visible.size > 0) {
      setActiveZone(ZONES.find((z) => visible.has(z.id))?.id ?? null);
    }

    return () => {
      observers.forEach((o) => o.disconnect());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (zoneId: string) => {
    const el = document.getElementById(zoneId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveZone(zoneId);
    }
  };

  const visibleZoneList = ZONES.filter((z) => visibleZones.has(z.id));
  if (visibleZoneList.length === 0) return null;

  return (
    <nav className="settings-anchor-strip" aria-label="Team settings sections">
      {visibleZoneList.map((zone) => (
        <button
          key={zone.id}
          type="button"
          className={`settings-anchor-strip__pill${
            activeZone === zone.id ? ' settings-anchor-strip__pill--active' : ''
          }`}
          onClick={() => handleClick(zone.id)}
          aria-current={activeZone === zone.id ? 'true' : undefined}
        >
          {zone.label}
        </button>
      ))}
    </nav>
  );
};
